import { EditorStarterItem } from '../../items/item-type';
import { TransitionItem } from '../../items/transition/transition-item-type';
import { TrackType } from '../../state/types';

/**
 * 트랜지션으로 연결된 아이템 그룹
 * TransitionSeries로 렌더링됨
 */
export interface TransitionGroup {
  type: 'transition-group';
  // 그룹에 포함된 아이템들 (시간 순서대로)
  items: EditorStarterItem[];
  // 아이템 사이의 트랜지션들 (items[i]와 items[i+1] 사이에 transitions[i])
  transitions: TransitionItem[];
  // 그룹의 시작 프레임
  startFrame: number;
  // 그룹의 총 duration (TransitionSeries 공식: 시퀀스 합 - 트랜지션 합)
  totalDurationInFrames: number;
}

/**
 * 트랜지션 없이 독립적으로 렌더링되는 아이템
 * 기존 Layer 컴포넌트로 렌더링됨
 */
export interface StandaloneItem {
  type: 'standalone';
  item: EditorStarterItem;
}

export type RenderableUnit = TransitionGroup | StandaloneItem;

/**
 * 트랙의 아이템들을 트랜지션 그룹과 독립 아이템으로 분류합니다.
 *
 * 알고리즘:
 * 1. 트랙의 아이템들을 from 순으로 정렬
 * 2. 각 아이템에 대해 다음 아이템과의 트랜지션이 있는지 확인
 * 3. 트랜지션으로 연결된 연속 체인을 그룹으로 묶음
 * 4. 트랜지션이 없는 아이템은 독립 아이템으로 처리
 *
 * @example
 * A -trans-> B -trans-> C    D    E -trans-> F
 * [   TransitionGroup    ] [Solo] [TransitionGroup]
 */
export function buildRenderableUnits(
  track: TrackType,
  items: Record<string, EditorStarterItem>,
  transitions: Record<string, TransitionItem>,
): RenderableUnit[] {
  // 트랙 아이템들을 가져와서 from 순으로 정렬
  const trackItems = track.items
    .map((id) => items[id])
    .filter((item): item is EditorStarterItem => !!item)
    .sort((a, b) => a.from - b.from);

  if (trackItems.length === 0) {
    return [];
  }

  // 이 트랙의 트랜지션을 sourceItemId로 인덱싱
  const transitionBySource = new Map<string, TransitionItem>();
  Object.values(transitions)
    .filter(
      (t) => t.trackId === track.id && t.transitionType !== 'capsuleCover',
    )
    .forEach((t) => transitionBySource.set(t.sourceItemId, t));

  const units: RenderableUnit[] = [];
  const processedIds = new Set<string>();

  for (let i = 0; i < trackItems.length; i++) {
    const startItem = trackItems[i];
    if (!startItem || processedIds.has(startItem.id)) {
      continue;
    }

    const transition = transitionBySource.get(startItem.id);

    if (!transition) {
      // 독립 아이템: 뒤쪽으로 연결된 트랜지션이 없음
      units.push({ type: 'standalone', item: startItem });
      processedIds.add(startItem.id);
      continue;
    }

    // 트랜지션 그룹 시작: 연결된 모든 아이템 수집
    const groupItems: EditorStarterItem[] = [startItem];
    const groupTransitions: TransitionItem[] = [];

    let currentItem = startItem;
    processedIds.add(currentItem.id);

    while (transitionBySource.has(currentItem.id)) {
      const trans = transitionBySource.get(currentItem.id)!;
      const nextItem = items[trans.targetItemId];

      if (!nextItem) {
        // targetItem이 없으면 체인 종료
        break;
      }

      groupTransitions.push(trans);
      groupItems.push(nextItem);
      processedIds.add(nextItem.id);
      currentItem = nextItem;
    }

    // TransitionSeries의 총 duration 계산
    //
    // UI에서 트랜지션은 두 클립 중앙에 배치됨 (source 끝 - duration/2)
    // TransitionSeries는 트랜지션을 시퀀스 끝에서 전체 duration만큼 겹침
    // 이를 보정하기 위해 각 시퀀스에 인접 트랜지션의 절반(duration/2)을 추가
    //
    // 시퀀스 effective duration = item.duration + nextTransHalf + prevTransHalf
    // 각 트랜지션 T에 대해 floor(T/2) + ceil(T/2) = T가 추가됨
    //
    // 따라서:
    // sum(effective durations) = sum(item.duration) + sum(transitionDurations)
    // TransitionSeries total = sum(effective) - sum(transitionDurations)
    //                        = sum(item.duration)
    //
    // 참고: 확장 정보(sourceExtendedFrames, targetExtendedFrames)는 totalDuration에 포함하지 않음
    // 확장은 트랜지션 영역 내에서 freeze 대신 실제 영상을 재생하는 용도임
    const totalItemDurations = groupItems.reduce(
      (sum, item) => sum + item.durationInFrames,
      0,
    );

    units.push({
      type: 'transition-group',
      items: groupItems,
      transitions: groupTransitions,
      startFrame: startItem.from,
      totalDurationInFrames: totalItemDurations,
    });
  }

  return units;
}
